import { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Table,
  Center,
  Loader,
  Button,
  Group,
  ActionIcon,
  Text,
} from "@mantine/core";
import { Link, useLocation } from "wouter";
import { deleteUser, getUsers, sendUserInvitation } from "../api";
import {
  IconCheck,
  IconPencil,
  IconRefresh,
  IconTrash,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import { useStore } from "../store";

// Mirrors UserController.Rank: a user may edit or delete only users who do not outrank them.
// Compared case-insensitively, like every other role/enum value from the backend.
const ROLE_RANK = { tenantuser: 1, tenantowner: 2, globaladmin: 3 };
const rankOf = (role) => ROLE_RANK[role?.toLowerCase()] ?? 0;
const outranks = (target, me) =>
  (ROLE_RANK[target?.toLowerCase()] ?? Infinity) > rankOf(me);

export default function UserList() {
  const [, setLocation] = useLocation();
  const [users, setUsers] = useState(null);
  const [userStale, setUserStale] = useState(0);
  const profile = useStore((state) => state.profile);

  useEffect(() => {
    getUsers().then((data) => {
      setUsers(data);
    });
  }, [userStale]);

  const openDeleteModal = (user) =>
    modals.openConfirmModal({
      title: `Delete user '${user.name ?? user.emailAddress}'`,
      centered: true,
      children: (
        <Text size="sm">
          Are you sure you want to delete this user? This action cannot be
          undone.
        </Text>
      ),
      labels: { confirm: "Delete user", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onCancel: () => console.log("Cancel"),
      onConfirm: () => {
        deleteUser(user.id).then(() => {
          setUserStale(userStale + 1);
        });
      },
    });

  if (!users)
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );

  return (
    <Container px={0}>
      <Grid justify="space-between">
        <Grid.Col span={12}>
          <Group justify="flex-end">
            <Button
              component={Link}
              to="/users/add"
              my="sm"
              leftSection={<IconUserPlus />}
            >
              Add new user
            </Button>
          </Group>
          <Table stickyHeader stickyHeaderOffset={60}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Email</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Created</Table.Th>
                {/* <Table.Th>Last Active</Table.Th> */}
                <Table.Th>Is Active</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.data?.map((p) => {
                const locked = outranks(p.userRole, profile?.userRole);
                const isSelf = p.id === profile?.userId;
                return (
                <Table.Tr key={p.id}>
                  <Table.Td>{p.emailAddress}</Table.Td>
                  <Table.Td>
                    {p.firstName} {p.lastName}
                  </Table.Td>
                  <Table.Td>{p.created}</Table.Td>
                  {/* <Table.Td>{p.lastActivity}</Table.Td> */}
                  <Table.Td>{p.isActive ? <IconCheck /> : <IconX />}</Table.Td>
                  <Table.Td>
                    <ActionIcon
                      size="sm"
                      mr="xs"
                      variant="outline"
                      component={Link}
                      disabled={p.isActive || locked}
                      to={p.isActive || locked ? "" : `/users/edit/${p.id}`}
                    >
                      <IconPencil size={14} />
                    </ActionIcon>

                    <ActionIcon
                      size="sm"
                      mr="xs"
                      disabled={p.isActive}
                      onClick={async () => {
                        await sendUserInvitation(p.id);
                        notifications.show({
                          color: "green",
                          autoClose: 3000,
                          title: "Invitation sent!",
                        });
                        setLocation("/users");
                      }}
                    >
                      <IconRefresh size={14} />
                    </ActionIcon>
                    <ActionIcon
                      size="sm"
                      mr="xs"
                      variant="outline"
                      color="red"
                      disabled={locked || isSelf}
                      onClick={() => {
                        openDeleteModal(p);
                      }}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
